import React from "react";

const KEYFRAMES_CSS = `
@keyframes focus-border-bottom-in {
    0%   { clip-path: inset(0 50% 0 50%); }
    100% { clip-path: inset(0 0 0 0); }
}
@keyframes focus-border-bottom-out {
    0%   { clip-path: inset(0 0 0 0); }
    100% { clip-path: inset(0 50% 0 50%); }
}
@keyframes focus-border-sides-in {
    0%   { clip-path: inset(100% 0 0 0); }
    100% { clip-path: inset(0 0 0 0); }
}
@keyframes focus-border-sides-out {
    0%   { clip-path: inset(0 0 0 0); }
    100% { clip-path: inset(100% 0 0 0); }
}
@keyframes focus-border-scale-in {
    0%   { transform: scaleX(0); }
    100% { transform: scaleX(1); }
}
@keyframes focus-border-scale-out {
    0%   { transform: scaleX(1); }
    100% { transform: scaleX(0); }
}
`;

interface AnimatedFocusBorderProps {
    focused: boolean;
}

/**
 * 4-sided animated focus border, used by `AmountInput` when `focusBorder`
 * is on. On focus the bottom snakes center-out, sides rise from below, top
 * fills edges-in. On blur it reverses: top retracts inside-out, sides drop,
 * bottom collapses center-in. The `focused` flag re-keys the wrapper so the
 * IN/OUT branch remounts cleanly each toggle.
 *
 * Consumer is responsible for `position: relative` on the parent.
 */
export function AnimatedFocusBorder({
    focused
}: AnimatedFocusBorderProps): React.ReactElement {
    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: KEYFRAMES_CSS }} />
            <div
                className="absolute inset-0 pointer-events-none"
                key={focused ? "focus-in" : "focus-out"}
            >
                {focused ? (
                    <>
                        {/* IN: bottom center-out → sides bottom-up → top edges-in */}
                        <div
                            className="absolute bottom-0 left-0 right-0 h-px bg-foreground-base"
                            style={{
                                animation:
                                    "focus-border-bottom-in 400ms ease-out 0ms both"
                            }}
                        />
                        <div
                            className="absolute top-0 bottom-0 left-0 w-px bg-foreground-base"
                            style={{
                                animation:
                                    "focus-border-sides-out 200ms linear 400ms reverse both"
                            }}
                        />
                        <div
                            className="absolute top-0 bottom-0 right-0 w-px bg-foreground-base"
                            style={{
                                animation:
                                    "focus-border-sides-out 200ms linear 400ms reverse both"
                            }}
                        />
                        <div
                            className="absolute top-0 left-0 w-1/2 h-px bg-foreground-base"
                            style={{
                                transformOrigin: "left",
                                animation:
                                    "focus-border-scale-in 400ms ease-in 600ms both"
                            }}
                        />
                        <div
                            className="absolute top-0 right-0 w-1/2 h-px bg-foreground-base"
                            style={{
                                transformOrigin: "right",
                                animation:
                                    "focus-border-scale-in 400ms ease-in 600ms both"
                            }}
                        />
                    </>
                ) : (
                    <>
                        {/* OUT: top inside-out → sides top-down → bottom outside-in (grey replaces) */}
                        <div
                            className="absolute top-0 left-0 w-1/2 h-px bg-foreground-base"
                            style={{
                                transformOrigin: "left",
                                animation:
                                    "focus-border-scale-out 400ms ease-out 0ms both"
                            }}
                        />
                        <div
                            className="absolute top-0 right-0 w-1/2 h-px bg-foreground-base"
                            style={{
                                transformOrigin: "right",
                                animation:
                                    "focus-border-scale-out 400ms ease-out 0ms both"
                            }}
                        />
                        <div
                            className="absolute top-0 bottom-0 left-0 w-px bg-foreground-base"
                            style={{
                                animation:
                                    "focus-border-sides-out 200ms linear 400ms both"
                            }}
                        />
                        <div
                            className="absolute top-0 bottom-0 right-0 w-px bg-foreground-base"
                            style={{
                                animation:
                                    "focus-border-sides-out 200ms linear 400ms both"
                            }}
                        />
                        <div
                            className="absolute bottom-0 left-0 right-0 h-px bg-foreground-base"
                            style={{
                                animation:
                                    "focus-border-bottom-out 400ms ease-in 600ms both"
                            }}
                        />
                    </>
                )}
            </div>
        </>
    );
}
